import {
    EuiButton,
    EuiButtonEmpty,
    EuiFlexGroup,
    EuiFlexItem,
    EuiPanel,
    EuiSelect,
    EuiText,
    EuiTitle,
} from "@elastic/eui";
import { useEffect, useState } from "react";
import React from "react";
import { useLocation } from "react-router";

import client from "../../api/Client";
import { useGetShopsByShopIdFromUserProfile } from "../../api/react-query/queries/shops";
import { PUBLIC_URL } from "../../Constants";

const ZOOM_STORAGE_KEY = "pricelist_preview_zoom_offset";

const resolutionMap: Record<string, { w: number; h: number }> = {
    "8k": { w: 7680, h: 4320 },
    "4k": { w: 3840, h: 2160 },
    "2k": { w: 2560, h: 1440 },
    "1080p": { w: 1920, h: 1080 },
    "720p": { w: 1280, h: 720 },
};

function parsePricelistPath(input: string) {
    const parts = input.split("/").filter(Boolean);

    if (parts[0] !== "pricelist") {
        return { shopId: null, screenId: null };
    }

    return {
        shopId: parts[1],
        screenId: parts[3],
    };
}

export const PricelistPreviewPage = () => {
    const [screenName, setScreenName] = useState("Preview");
    const [resolution, setResolution] = useState("1080p");

    const query = new URLSearchParams(useLocation().search);
    const encoded = query.get("url") ?? "";
    const decodedUrl = decodeURIComponent(encoded);
    const previewUrl = PUBLIC_URL + decodedUrl;
    const { shopId, screenId } = parsePricelistPath(decodedUrl);
    const isPortrait = decodedUrl.includes("portrait");
    const defaultScale = isPortrait ? 0.4 : 0.66;
    const [zoomOffset, setZoomOffset] = useState(() => {
        const saved = localStorage.getItem(ZOOM_STORAGE_KEY);
        return saved ? parseFloat(saved) : 0;
    });
    const [iframeError, setIframeError] = useState(false);

    useEffect(() => {
        if (screenId) {
            console.log("Fetching screen info for ID:", screenId);
            client.get(`/v1/screens/${screenId}`).then((response) => {
                setScreenName(response.data.name);
                document.title = response.data.name;
            });
        }
    }, [screenId]);

    const shopName = useGetShopsByShopIdFromUserProfile(shopId ?? "");

    if (!shopId || !shopName) {
        return <EuiText>Error loading preview, please provide valid url.</EuiText>;
    }

    const handleZoomIn = () =>
        setZoomOffset((z) => {
            const newValue = z + 0.04;
            localStorage.setItem(ZOOM_STORAGE_KEY, newValue.toString());
            return newValue;
        });

    const handleZoomOut = () =>
        setZoomOffset((z) => {
            const newValue = Math.max(-defaultScale + 0.04, z - 0.04);
            localStorage.setItem(ZOOM_STORAGE_KEY, newValue.toString());
            return newValue;
        });

    const handleResetZoom = () => {
        setZoomOffset(0);
        localStorage.setItem(ZOOM_STORAGE_KEY, "0");
    };

    const base = resolutionMap[resolution];
    const width = isPortrait ? base.h : base.w;
    const height = isPortrait ? base.w : base.h;

    // Auto-scaling based on resolution
    const autoScaleFactor = 1920 / width;

    // Combine defaults + user zoom
    const finalScale = (defaultScale + zoomOffset) * autoScaleFactor;

    return (
        <EuiPanel paddingSize="l">
            <EuiFlexGroup gutterSize="m" style={{ marginBottom: 4 }} alignItems="center">
                <EuiFlexItem grow={false}>
                    <EuiTitle size="l">
                        <h1 style={{ marginBottom: "4px", marginRight: "16px" }}>{screenName}</h1>
                    </EuiTitle>
                </EuiFlexItem>
                {shopName && (
                    <EuiFlexItem grow={false}>
                        <EuiText>
                            <h4 style={{ marginBottom: "4px", marginRight: "8px" }}>{shopName}</h4>
                        </EuiText>
                    </EuiFlexItem>
                )}

                <EuiFlexItem grow={false}>
                    <EuiSelect
                        options={[
                            { value: "8k", text: "8K (7680×4320)" },
                            { value: "4k", text: "4K (3840×2160)" },
                            { value: "2k", text: "2K (2560×1440)" },
                            { value: "1080p", text: "1080p (1920×1080)" },
                            { value: "720p", text: "720p (1280×720)" },
                        ]}
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        aria-label="Select preview resolution"
                    />
                </EuiFlexItem>

                <EuiFlexItem grow={false}>
                    <EuiButton size="s" iconType="magnifyWithMinus" onClick={handleZoomOut}>
                        Zoom Out
                    </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButton size="s" iconType="magnifyWithPlus" onClick={handleZoomIn}>
                        Zoom In
                    </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButtonEmpty size="xs" iconType="refresh" onClick={handleResetZoom}>
                        Reset Zoom
                    </EuiButtonEmpty>
                </EuiFlexItem>
            </EuiFlexGroup>

            <div
                style={{
                    width,
                    height,
                    transform: `scale(${finalScale})`,
                    transformOrigin: "top left",
                }}
            >
                {iframeError && (
                    <EuiText color="danger">
                        Failed to load preview. This may be blocked by browser security settings.
                    </EuiText>
                )}

                <iframe
                    src={previewUrl}
                    width={width}
                    height={height}
                    onError={() => setIframeError(true)}
                    style={{ border: "none" }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
            </div>
        </EuiPanel>
    );
};
